import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from "react"

import {
    PoliciesListType,
    PolicyAssignmentStatus,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper.tsx"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"

import { GENERAL_CONFIG } from "../../../../config/general.config.ts"
import { POLICIES_CONFIG } from "../../../../config/policies.config"
import { ButtonDropdownElement } from "../../../../elements/buttonDropdown.element.tsx"
import { RuntimeHelper } from "../../../../helpers/runtime.helper"
import { useHasPermissions } from "../../../../hooks/hasPermissions.hook"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { policiesSelector } from "../../../../store/selectors/policies.selector"
import { runtimeSelector } from "../../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../../store/selectors/selectedPracticeRole.selector"
import { userDetailsSelector } from "../../../../store/selectors/userDetails.selector"
import {
    policiesSetSelectedItem,
    policiesUpdateSelectedItemHistory,
} from "../../../../store/slices/policies.slice.ts"
import { taskAssignmentsUpdateStatus } from "../../../../store/thunks/taskAssignments.thunks.ts"

import arrowDownIcon from "@front-shared/assets/images/icons/chevron-down-dark.svg"

export const TaskAssignmentStatusChange: React.FunctionComponent<
    ITaskAssignmentStatusChangeProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const userDetailsState = useAppSelector(userDetailsSelector)
    const { selectedItem } = useAppSelector(policiesSelector)

    const hasManageTaskAssignmentsPermission = useHasPermissions(
        OneVerifyPermission.manageTaskAssignments,
        undefined,
        false,
    )

    const hasWorkTaskAssignmentsPermission = useHasPermissions(
        OneVerifyPermission.workTaskAssignments,
        undefined,
        false,
    )

    //////// LOCAL STATES

    const [currentTaskAssignmentStatus, setCurrentTaskAssignmentStatus] =
        useState<PolicyAssignmentStatus | undefined>()

    //////// MEMOS

    const isUpdatingTaskAssignmentStatusLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isTaskAssignmentsUpdateStatusLoading()
    }, [runtimeState.isLoading])

    const canRenderTaskAssignmentStatusDropdown = useMemo<boolean>(() => {
        if (
            !userDetailsState.profile ||
            !selectedItem?.policy?.latestAssignment ||
            !POLICIES_CONFIG.activeAssignmentStatuses.includes(
                selectedItem.policy.latestAssignment.status,
            )
        ) {
            return false
        }

        return (
            hasManageTaskAssignmentsPermission ||
            selectedItem.policy.latestAssignment.assignee.id ===
                userDetailsState.profile.id
        )
    }, [
        hasManageTaskAssignmentsPermission,
        hasWorkTaskAssignmentsPermission,
        userDetailsState.profile?.id,
        selectedItem?.policy?.latestAssignment?.assignee?.id,
    ])

    //////// EFFECTS

    // Update the task assignment status when selectedItem changes
    useEffect(() => {
        setCurrentTaskAssignmentStatus(
            selectedItem?.policy?.latestAssignment?.status,
        )
    }, [selectedItem?.policy?.latestAssignment?.status])

    //////// METHODS

    const onTaskAssignmentStatusChange = (status: PolicyAssignmentStatus) => {
        if (status === currentTaskAssignmentStatus) {
            // Show a warning
            ToastrHelper.warning(
                "Selected policy already has this status. No updates are needed.",
                GENERAL_CONFIG.defaultToastrTimeout,
                undefined,
            )

            return
        }

        ConfirmationHelper.enableConfirmation(
            () => {
                if (
                    !selectedPracticeRole ||
                    !selectedItem?.policy?.latestAssignment?.id
                ) {
                    return
                }

                dispatch(
                    taskAssignmentsUpdateStatus({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            assignment: {
                                id: selectedItem.policy.latestAssignment.id,
                                nirvanaRequestId:
                                    selectedItem.coverageResult.resultId,
                            },
                            status,
                        },
                        onSuccess: (
                            result: IOneVerifyCoverageCheckHistory | null,
                        ) => {
                            props.setTasksListTypeNeedsReload(true)

                            if (!result) {
                                return
                            }

                            setCurrentTaskAssignmentStatus(status)
                            dispatch(policiesSetSelectedItem(result))
                            dispatch(policiesUpdateSelectedItemHistory(result))
                        },
                    }),
                )
            },
            "Confirm Status Change",
            `Assignment status will be changed${currentTaskAssignmentStatus ? ` from "${POLICIES_CONFIG.policyAssignmentStatusMapping(props.listType)[currentTaskAssignmentStatus] ?? currentTaskAssignmentStatus}"` : ""} to "${POLICIES_CONFIG.policyAssignmentStatusMapping(props.listType)[status] ?? status}".`,
        )
    }

    return POLICIES_CONFIG.tasksListTypes.includes(props.listType) &&
        canRenderTaskAssignmentStatusDropdown ? (
        <div className="ml-[16px] sm:ml-[0px] sm:mt-[8px]">
            <ButtonDropdownElement
                buttonLabel={
                    currentTaskAssignmentStatus
                        ? (POLICIES_CONFIG.policyAssignmentStatusMapping(
                              props.listType,
                          )[currentTaskAssignmentStatus] ??
                          "Change Task Status")
                        : "Change Task Status"
                }
                options={(() => {
                    const mapping =
                        POLICIES_CONFIG.policyAssignmentStatusMapping(
                            props.listType,
                        )

                    // Map each status to a dropdown option, returning null for statuses not in the mapping
                    // Then filter out null values to only show statuses available for this listType
                    return POLICIES_CONFIG.uiAllowedAssignmentStatuses
                        .map(status => {
                            const label = mapping[status]

                            if (!label) {
                                return null
                            }

                            return {
                                label,
                                onClick: () => {
                                    onTaskAssignmentStatusChange(status)
                                },
                                isSelected:
                                    currentTaskAssignmentStatus === status,
                            }
                        })
                        .filter(
                            (option): option is NonNullable<typeof option> =>
                                option !== null,
                        )
                })()}
                buttonType="default"
                buttonIcon={arrowDownIcon}
                buttonIsDisabled={false}
                buttonIsLoading={isUpdatingTaskAssignmentStatusLoading}
                buttonIsRightIcon={true}
            />
        </div>
    ) : null
}

interface ITaskAssignmentStatusChangeProps {
    className?: string
    listType: PoliciesListType
    setTasksListTypeNeedsReload: Dispatch<SetStateAction<boolean>>
}
